import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, GraduationCap, Building2, Check } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const PATHS = [
  {
    to: "/courses",
    num: "01",
    icon: GraduationCap,
    overline: "The Academy",
    title: "Courses & Training",
    description: "22 practice-based SAP S/4HANA programs — live weekend cohorts, self-paced video courses, and certification preparation.",
    points: ["Live cohorts with 1-year recordings", "Self-paced videos from $50", "98% certification pass rate"],
    cta: "Explore the course catalog",
    testid: "path-card-courses",
  },
  {
    to: "/consulting",
    num: "02",
    icon: Building2,
    overline: "The Practice",
    title: "Consulting & Advisory",
    description: "Senior-led SAP S/4HANA transformation for the office of the CFO — greenfield builds, ECC conversion, AMS, RISE with SAP, and global rollouts.",
    points: ["Greenfield & system conversion", "AMS & continuous optimization", "RISE / GROW with SAP"],
    cta: "Explore consulting services",
    testid: "path-card-consulting",
  },
];

const LandingPaths = () => {
  return (
    <section className="relative py-24 lg:py-36" data-testid="landing-paths">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.25em] text-amber-600 mb-5"
        >
          Choose Your Path
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-16 max-w-2xl"
        >
          What brings you here today?
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-6">
          {PATHS.map((p, i) => (
            <motion.div
              key={p.to}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, ease: EASE, delay: i * 0.12 }}
            >
              <Link
                to={p.to}
                data-testid={p.testid}
                className="group relative flex flex-col h-full rounded-3xl border border-slate-200 bg-white p-8 lg:p-12 hover:-translate-y-2 hover:border-amber-600/40 hover:shadow-[0_30px_80px_-30px_rgba(217,119,6,0.25)] transition-all duration-500"
              >
                <div className="flex items-start justify-between mb-10">
                  <span className="w-14 h-14 rounded-2xl bg-amber-600/10 border border-amber-600/25 flex items-center justify-center">
                    <p.icon size={24} className="text-amber-600" />
                  </span>
                  <span className="font-mono2 text-5xl font-bold text-slate-900/[0.07] group-hover:text-amber-600/30 transition-colors duration-500">
                    {p.num}
                  </span>
                </div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">{p.overline}</p>
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-4">{p.title}</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-8">{p.description}</p>
                <ul className="space-y-2.5 mb-10 flex-1">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <span className="w-4.5 h-4.5 w-5 h-5 rounded-full bg-amber-600/10 border border-amber-600/25 flex items-center justify-center flex-shrink-0">
                        <Check size={10} className="text-amber-600" />
                      </span>
                      {pt}
                    </li>
                  ))}
                </ul>
                <span className="inline-flex items-center gap-2 font-semibold text-sm text-slate-900 group-hover:text-amber-700 transition-colors duration-300">
                  {p.cta}
                  <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LandingPaths;
